import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import {
  Info, CreditCard, RotateCcw, CheckCircle,
  Shield, DollarSign, X,
} from "react-feather";

/**
 * PaymentSettingsHelpDrawer
 *
 * ROOT CAUSE FIXES:
 * 1. Replaced Bootstrap <Modal> entirely with a custom portal-rendered drawer.
 *    Bootstrap's Modal appends a .modal-backdrop to document.body and sets
 *    body { overflow: hidden } — when z-indexes clash, the backdrop sits over
 *    the parent page and blocks all pointer events (the "unresponsive page" bug).
 *
 * 2. This component uses ReactDOM.createPortal(…, document.body) directly,
 *    giving us full control over z-index, backdrop, and scroll-lock without
 *    Bootstrap interference.
 *
 * 3. The drawer is right-aligned at exactly 50vw width, with the backdrop
 *    only covering the LEFT half — so the parent page is still partially visible.
 *
 * 4. Escape key and backdrop click both close the drawer cleanly.
 *
 * 5. Body scroll is locked only for the drawer itself (overflowY on the panel),
 *    never on document.body — so the parent page stays interactive.
 */

const TABS = ["Overview", "Payment modes", "Refund rules", "Examples", "Recommended"];

/* ─── Tiny shared sub-components ─────────────────────────────────────────── */

function SecLabel({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.13em",
      textTransform: "uppercase", color: "#7a93ad", marginBottom: 12,
      display: "flex", alignItems: "center", gap: 8,
    }}>
      {children}
      <span style={{ flex: 1, height: 1, background: "rgba(122,147,173,0.2)", display: "block" }} />
    </div>
  );
}

function Ico({ children }) {
  return (
    <div style={{
      width: 30, height: 30, minWidth: 30, borderRadius: 7,
      background: "#eef4ff", border: "1px solid #d4e0f8",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {children}
    </div>
  );
}

function HelpCard({ accent, children, style }) {
  return (
    <div style={{
      background: "#fff",
      border: `1px solid rgba(205,214,228,0.9)`,
      borderLeft: accent ? "3px solid #183777" : undefined,
      borderRadius: 12,
      padding: "16px 18px",
      marginBottom: 11,
      ...style,
    }}>
      {children}
    </div>
  );
}

function CardHead({ icon: Icon, title }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 9 }}>
      <Ico><Icon size={14} color="#183777" /></Ico>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#12233f" }}>{title}</div>
    </div>
  );
}

function Cx({ children, style }) {
  return (
    <div style={{ fontSize: 13, lineHeight: 1.7, color: "#4c5d79", ...style }}>
      {children}
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div style={{
      background: "#fff", border: "1px solid rgba(205,214,228,0.9)",
      borderRadius: 9, padding: 13,
    }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "#7b8fb8", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 12.5, lineHeight: 1.6, color: "#334765", fontWeight: 500 }}>
        {value}
      </div>
    </div>
  );
}

const BADGE = {
  green: { bg: "#edf7ee", color: "#2d7a3a", border: "#c3e6c8" },
  blue:  { bg: "#e8f0fb", color: "#2155a3", border: "#c2d4f5" },
  amber: { bg: "#fdf3e4", color: "#9a6110", border: "#f5ddb0" },
  red:   { bg: "#fde8e8", color: "#9a2020", border: "#f5b0b0" },
};
const EX_BG = {
  green: { background: "#f0f7f1", borderColor: "#c3e6c8" },
  blue:  { background: "#f0f4fb", borderColor: "#c2d4f5" },
  amber: { background: "#fdf8f0", borderColor: "#f5ddb0" },
  red:   { background: "#fdf0f0", borderColor: "#f5b0b0" },
};

function ExCard({ title, badge, tone }) {
  const b = BADGE[tone];
  const e = EX_BG[tone];
  return (
    <div style={{ ...e, borderRadius: 12, padding: 15, border: "1px solid", borderColor: e.borderColor }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 7 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "#162846" }}>{title}</div>
        <span style={{
          fontSize: 10.5, fontWeight: 600, padding: "3px 9px",
          borderRadius: 999, border: `1px solid ${b.border}`,
          background: b.bg, color: b.color, whiteSpace: "nowrap", flexShrink: 0,
        }}>
          {badge}
        </span>
      </div>
    </div>
  );
}

/* ─── Tab content panels ──────────────────────────────────────────────────── */

function TabOverview() {
  return (
    <>
      <SecLabel>Overview</SecLabel>
      <HelpCard accent>
        <CardHead icon={Info} title="What is this page for?" />
        <Cx>
          This page decides how a guest should pay when making a booking and what should
          happen if that booking is cancelled. These settings affect guest confidence,
          booking conversion, and revenue protection.
        </Cx>
      </HelpCard>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 14 }}>
        <Mini label="Use this for"   value="Payment collection & confirmation rules" />
        <Mini label="Refund logic"   value="What guest gets back on cancellation" />
        <Mini label="Best practice"  value="Keep policy simple and transparent" />
      </div>
    </>
  );
}

function TabPaymentModes() {
  return (
    <>
      <SecLabel>Payment modes</SecLabel>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 11 }}>
        <HelpCard><CardHead icon={DollarSign} title="Pay at property" /><Cx>Guest books now, pays on arrival. No upfront collection. Higher no-show risk.</Cx></HelpCard>
        <HelpCard><CardHead icon={CreditCard} title="Deposit / advance" /><Cx>Collect a partial amount now. Secures booking while keeping conversion high.</Cx></HelpCard>
        <HelpCard><CardHead icon={Shield}     title="Full prepayment"  /><Cx>Full amount charged at booking. Maximum revenue certainty for the hotel.</Cx></HelpCard>
        <HelpCard><CardHead icon={Shield}     title="Card guarantee"   /><Cx>Card held on file. Charged only on confirmed no-show or late cancellation.</Cx></HelpCard>
      </div>
    </>
  );
}

function TabRefundRules() {
  return (
    <>
      <SecLabel>Refund rules</SecLabel>
      <HelpCard style={{ marginBottom: 11 }}>
        <CardHead icon={RotateCcw} title="How refund rules work" />
        <Cx style={{ marginBottom: 14 }}>
          Stack multiple time-based windows before check-in. Each defines the refund
          percentage applied to the collected amount.
        </Cx>
        <div style={{ borderLeft: "2px solid #183777", paddingLeft: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          {[
            { label: "5+ days before check-in",   text: "100% refund — full amount returned to guest." },
            { label: "48 hours before check-in",  text: "50% refund — hotel retains the remainder." },
            { label: "Inside last 48 hours",       text: "No refund — full amount retained by hotel." },
          ].map(({ label, text }) => (
            <div key={label}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#12233f", marginBottom: 2 }}>{label}</div>
              <Cx>{text}</Cx>
            </div>
          ))}
        </div>
      </HelpCard>
      <HelpCard>
        <CardHead icon={Info} title="Simple rule of thumb" />
        <Cx>Earlier cancellation = higher refund. Closer to arrival = lower refund.</Cx>
      </HelpCard>
    </>
  );
}

function TabExamples() {
  return (
    <>
      <SecLabel>Policy examples</SecLabel>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 11 }}>
        {[
          { title: "Flexible policy",     badge: "Popular",         tone: "green" },
          { title: "Balanced protection", badge: "Recommended",     tone: "blue"  },
          { title: "Deposit-based",       badge: "Advance booking", tone: "amber" },
          { title: "Non-refundable",      badge: "Strict",          tone: "red"   },
        ].map(ex => <ExCard key={ex.title} {...ex} />)}
      </div>
    </>
  );
}

function TabRecommended() {
  return (
    <>
      <SecLabel>Recommended setup</SecLabel>
      <div style={{
        background: "#0f1f4d", borderRadius: 12, padding: "20px 22px",
        marginBottom: 11, display: "flex", gap: 14, alignItems: "flex-start",
      }}>
        <div style={{
          width: 36, height: 36, minWidth: 36, borderRadius: 9,
          background: "rgba(201,168,76,0.15)", border: "1px solid rgba(201,168,76,0.3)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <CheckCircle size={17} color="#c9a84c" />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#fff", marginBottom: 5 }}>
            What should most hotels choose?
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", lineHeight: 1.7 }}>
            Deposit or full prepay with a tiered cancellation window offers the best balance
            of revenue protection and guest transparency.
          </div>
        </div>
      </div>
      <HelpCard>
        <CardHead icon={Info} title="Operational advice" />
        <Cx>
          Keep guest-facing policy language short and exact. Avoid complex rule combinations
          unless your team can explain them consistently at front desk and during support.
        </Cx>
      </HelpCard>
    </>
  );
}

const TAB_PANELS = [TabOverview, TabPaymentModes, TabRefundRules, TabExamples, TabRecommended];

/* ─── Main drawer component ──────────────────────────────────────────────── */

export default function PaymentSettingsHelpDrawer({ show, onHide }) {
  const [tab, setTab] = useState(0);
  const [visible, setVisible] = useState(false);   // controls CSS transition
  const [mounted, setMounted] = useState(false);   // controls DOM presence

  /* Mount → trigger enter transition */
  useEffect(() => {
    if (show) {
      setMounted(true);
      // rAF so the initial transform is applied before the transition class
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    } else {
      setVisible(false);
      // Unmount after transition completes (300ms)
      const t = setTimeout(() => setMounted(false), 320);
      return () => clearTimeout(t);
    }
  }, [show]);

  /* Escape key — close drawer WITHOUT affecting the parent page */
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") onHide();
  }, [onHide]);

  useEffect(() => {
    if (show) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, handleKeyDown]);

  /* 
    CRITICAL FIX: We do NOT lock document.body scroll.
    The drawer panel has its own internal scroll (overflowY: auto on the body section).
    This means the parent page behind the drawer stays fully interactive and scrollable,
    which is what prevents the "page becomes unresponsive" bug.
  */

  if (!mounted) return null;

  const ActivePanel = TAB_PANELS[tab] || TabOverview;

  return ReactDOM.createPortal(
    <>
      {/* ── Backdrop: left-side dimmer only, right side is the drawer ── */}
      <div
        onClick={onHide}
        style={{
          position: "fixed",
          inset: 0,
          /* Only dim the left half — the drawer covers the right 50vw */
          background: "rgba(10,20,50,0.45)",
          /* 
            CRITICAL FIX: z-index 9998 — below the drawer (9999) but ABOVE
            all Bootstrap modals (z-index ~1050) and Bootstrap backdrops (z-index ~1040).
            This ensures our backdrop never gets buried under a stale Bootstrap backdrop.
          */
          zIndex: 9998,
          transition: "opacity 0.3s ease",
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? "auto" : "none",
        }}
        aria-hidden="true"
      />

      {/* ── Drawer panel ─────────────────────────────────────────────── */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Payment settings help"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          /*
            50vw width, minimum 440px so it never collapses on small screens.
            No left: auto needed — right:0 + width handles positioning.
          */
          width: "50vw",
          minWidth: 440,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          background: "#f0f3f8",
          boxShadow: "-16px 0 60px rgba(10,20,50,0.25)",
          /* Slide-in from the right */
          transform: visible ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.3s cubic-bezier(0.32,0.72,0,1)",
          /* Drawer itself never scrolls — only its inner body pane does */
          overflow: "hidden",
        }}
      >
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div style={{ flexShrink: 0 }}>
          {/* Gold accent bar */}
          <div style={{ height: 3, background: "linear-gradient(90deg,#0f1f4d,#c9a84c,#0f1f4d)" }} />

          {/* Hero */}
          <div style={{
            background: "#0f1f4d",
            padding: "24px 28px 0",
            position: "relative",
            overflow: "hidden",
          }}>
            {/* Decorative radial */}
            <div style={{
              position: "absolute", right: -80, bottom: -80,
              width: 220, height: 220, borderRadius: "50%",
              background: "radial-gradient(circle, rgba(255,255,255,0.06), transparent 65%)",
              pointerEvents: "none",
            }} />

            {/* Top row: text + close */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 14 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Eyebrow */}
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "5px 12px", borderRadius: 999,
                  background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.28)",
                  color: "#fbbf24", fontSize: 10, fontWeight: 600,
                  letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 11,
                }}>
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#fbbf24", display: "inline-block" }} />
                  Configuration guide
                </div>

                <h2 style={{ fontSize: 22, fontWeight: 600, color: "#fff", letterSpacing: "-0.02em", lineHeight: 1.2, margin: "0 0 7px" }}>
                  Payment &amp; refund settings
                </h2>
                <p style={{ fontSize: 13, color: "rgba(255,255,255,0.58)", lineHeight: 1.65, margin: 0 }}>
                  Guidance for hotel teams to configure booking payments, cancellation windows, and refund behaviour.
                </p>
              </div>

              {/* 
                CRITICAL FIX: Close button uses explicit pointer-events:all and a high
                z-index to ensure it is NEVER blocked by the backdrop or any overlay layer.
                The original bug had the backdrop's z-index occasionally sitting above
                the close button, making it unclickable.
              */}
              <button
                type="button"
                onClick={onHide}
                aria-label="Close help drawer"
                style={{
                  width: 40, height: 40, minWidth: 40,
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.15)",
                  background: "rgba(255,255,255,0.09)",
                  color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer",
                  position: "relative",
                  zIndex: 10000,          /* above everything */
                  pointerEvents: "all",   /* never blocked */
                  flexShrink: 0,
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.09)"}
              >
                <X size={16} />
              </button>
            </div>

            {/* Tab bar */}
            <div style={{ display: "flex", overflowX: "auto", scrollbarWidth: "none" }}>
              {TABS.map((t, i) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(i)}
                  style={{
                    padding: "10px 16px",
                    border: "none",
                    background: "transparent",
                    color: tab === i ? "#fbbf24" : "rgba(255,255,255,0.45)",
                    fontSize: 12, fontWeight: 500,
                    cursor: "pointer",
                    borderBottom: `2px solid ${tab === i ? "#fbbf24" : "transparent"}`,
                    whiteSpace: "nowrap",
                    fontFamily: "inherit",
                    transition: "color 0.15s, border-color 0.15s",
                    flexShrink: 0,
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Body (the ONLY scrollable zone) ────────────────────────── */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "22px 28px",
          background: "#f0f3f8",
          /* Custom scrollbar */
          scrollbarWidth: "thin",
          scrollbarColor: "#c8d4e4 transparent",
        }}>
          <ActivePanel />
        </div>

        {/* ── Footer ─────────────────────────────────────────────────── */}
        <div style={{
          flexShrink: 0,
          background: "#fff",
          borderTop: "1px solid #dde5f0",
          padding: "14px 28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}>
          <div style={{ fontSize: 12, color: "#7a93ad", display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", display: "inline-block", flexShrink: 0 }} />
            Changes apply to new bookings only
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={onHide}
              style={{
                padding: "9px 18px", borderRadius: 8,
                border: "1px solid #c8d4e4", background: "#fff",
                color: "#4a6580", fontSize: 13, fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              Close
            </button>
            <button
              type="button"
              onClick={onHide}
              style={{
                padding: "9px 20px", borderRadius: 8,
                border: "none", background: "#0f1f4d",
                color: "#fff", fontSize: 13, fontWeight: 600,
                fontFamily: "inherit", cursor: "pointer",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#183777"}
              onMouseLeave={e => e.currentTarget.style.background = "#0f1f4d"}
            >
              Understood
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body   /* Portal target — outside any React subtree that could clip z-index */
  );
}